import { forwardRef } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { cn } from '../utils/cn';

/** One tone vocabulary across the glass layer — `StatTile` uses the same names. */
export type PillTone = 'default' | 'neutral' | 'accent' | 'positive' | 'caution' | 'critical';

const toneColor: Record<PillTone, string> = {
  default: 'var(--g-text-secondary)',
  neutral: 'var(--g-text-secondary)',
  accent: 'var(--g-accent)',
  positive: 'var(--g-positive)',
  caution: 'var(--g-caution)',
  critical: 'var(--g-critical)',
};

export interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: PillTone;
  /** Leading status dot. On by default — it is what makes a chip readable at a glance. */
  dot?: boolean;
  /** Slow halo behind the dot, for "live" and "running" states only. */
  pulse?: boolean;
  size?: 'sm' | 'md';
}

/**
 * A status chip. Tinted from a single token via `color-mix`, so tone, border and
 * dot always agree and no combination has to be hand-picked.
 */
export const Pill = forwardRef<HTMLSpanElement, PillProps>(function Pill(
  { tone = 'neutral', dot = true, pulse = false, size = 'md', className, style, children, ...rest },
  ref,
) {
  const reduceMotion = useReducedMotion();
  const color = toneColor[tone];

  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-[12px]',
        className,
      )}
      style={{
        color,
        background: `color-mix(in oklab, ${color} 14%, transparent)`,
        border: `1px solid color-mix(in oklab, ${color} 30%, transparent)`,
        ...style,
      }}
      {...rest}
    >
      {dot ? (
        <span className="relative flex h-1.5 w-1.5 shrink-0" aria-hidden="true">
          {pulse && !reduceMotion ? (
            <motion.span
              className="absolute inset-0 rounded-full"
              style={{ background: color }}
              animate={{ scale: [1, 2.4, 1], opacity: [0.55, 0, 0.55] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
            />
          ) : null}
          <span className="relative h-1.5 w-1.5 rounded-full" style={{ background: color }} />
        </span>
      ) : null}
      {children}
    </span>
  );
});
